import React from 'react';
import useOrganizationGroups from '../../../../../../hooks/api/useOrganizationGroups';
import { TableConfig } from '../../../../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import { useRichTableState } from '../../../../../v2BaseComponents/RichTable/useRichTableState';
import RichTable from '../../../../../v2BaseComponents/RichTable';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import AddGroupSideDrawerContent from './AddGroupSideDrawerContent';
import { useOrganizationTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useIsOrganizationAdmin } from '../../../../../../hooks/api/useOrganization';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import InfoIcon from '../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../v2BaseComponents/Prompt';
import EditGroupSideDrawerContent from './EditGroupSideDrawerContent';
import GroupDetailsDrawerContent from './GroupDetailsDrawerContent';

const intl = getIntl('resources-root-screen-group-tab-table', {
    addGroup: 'Add Group',
    editGroup: 'Edit Group',
    groupDetails: 'Group Details',
    name: 'Name',
    description: 'Description',
    deleteGroupHeader: 'Delete Group?',
    deleteGroupContent: 'Are you sure you want to delete the group "{name}"?  This action cannot be undone.',
    deleteGroupConfirm: 'Delete Group',
    cancel: 'Cancel',
    deleteGroupsHeader: 'Delete Groups?',
    deleteGroupsContent: 'Are you sure you want to delete {count} groups?  This action cannot be undone.',
    deleteGroupsConfirm: 'Delete Groups',
    delete: 'Delete',
});

export type GroupsTableOwnProps = {};

export type GroupsTableProps = GroupsTableOwnProps;

const GroupsTable: React.FC<GroupsTableProps> = (_props) => {
    const organizationTabNavigator = useOrganizationTabNavigation();
    const [tableState, setTableState] = useRichTableState();
    const { groups, isFetchingGroups, deleteGroup, deleteGroups, isDeletingGroups, isDeletingGroup } =
        useOrganizationGroups({});
    const getName = (group: APITypes.organizationService.OrganizationGroupDetails) => group.metadata.name;
    const getDescription = (group: APITypes.organizationService.OrganizationGroupDetails) =>
        group.metadata.description || null;
    const groupDetailsSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'groupDetails',
        title: intl('groupDetails'),
        renderContent: () => <GroupDetailsDrawerContent groupId={tableState.selectedRowKeys[0]} />,
        mobileOnFocus: () => organizationTabNavigator.navigate('group', { groupId: tableState.selectedRowKeys[0] }),
    });
    const addGroupSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'addGroup',
        title: intl('addGroup'),
        renderContent: (onClose) => <AddGroupSideDrawerContent onClose={onClose} />,
        mobileOnFocus: () => organizationTabNavigator.navigate('add-group'),
    });
    const editGroupSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'editGroup',
        title: intl('editGroup'),
        renderContent: (onClose) => (
            <EditGroupSideDrawerContent onClose={onClose} groupId={tableState.selectedRowKeys[0]} />
        ),
        mobileOnFocus: () =>
            organizationTabNavigator.navigate('edit-group', { groupId: tableState.selectedRowKeys[0] }),
    });
    const isOrganizationAdmin = useIsOrganizationAdmin();
    const selectedGroups = tableState.selectedRowKeys.map((key) =>
        groups.find((group) => group.organizationGroupId === key),
    );
    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <InfoIcon
                    alertIconProps={{
                        name: 'AlertTriangle',
                        variant: 'error',
                    }}
                    title={
                        tableState.selectedRowKeys.length === 1 ? intl('deleteGroupHeader') : intl('deleteGroupsHeader')
                    }
                    subText={
                        tableState.selectedRowKeys.length === 1
                            ? intl('deleteGroupContent', { name: selectedGroups[0]?.metadata.name || '' })
                            : intl('deleteGroupsContent', { count: tableState.selectedRowKeys.length })
                    }
                />
                <PromptActions
                    actions={[
                        {
                            title:
                                tableState.selectedRowKeys.length === 1
                                    ? intl('deleteGroupConfirm')
                                    : intl('deleteGroupsConfirm'),
                            onPress: () => {
                                if (tableState.selectedRowKeys.length === 1) {
                                    deleteGroup(tableState.selectedRowKeys[0]);
                                } else {
                                    deleteGroups(tableState.selectedRowKeys);
                                }
                                onClose();
                            },
                            variant: 'alert',
                            icon: 'Trash2',
                            testID: 'delete-group-confirm',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'delete-group-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: tableState.selectedRowKeys.length === 1 ? intl('deleteGroupHeader') : intl('deleteGroupsHeader'),
    });

    const groupsTableConfig: TableConfig<APITypes.organizationService.OrganizationGroupDetails> = {
        columns: [
            {
                label: intl('name'),
                columnKey: 'name',
                getSortValue: getName,
                getContentKey: getName,
                getSearchValue: getName,
                render: getName,
            },
            {
                label: intl('description'),
                columnKey: 'description',
                getSortValue: getDescription,
                getContentKey: (item) => getDescription(item) || '',
                getSearchValue: getDescription,
                render: getDescription,
                hideOnMobile: true,
            },
        ],
        getRowKey: (group) => group.organizationGroupId,
    };

    return (
        <>
            <RichTable
                config={groupsTableConfig}
                state={tableState}
                onStateChange={setTableState}
                data={groups}
                minWidth={'100%'}
                allowRowSelection
                style={{ height: '100%' }}
                isFetching={isFetchingGroups}
                actions={[
                    {
                        title: intl('addGroup'),
                        icon: 'PlusCircle',
                        onPress: () => {
                            addGroupSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 0,
                        testID: 'add-group-button',
                    },
                    {
                        title: intl('groupDetails'),
                        icon: 'Info',
                        onPress: () => {
                            groupDetailsSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => selected.length === 1,
                        testID: 'group-details',
                    },
                    {
                        title: intl('editGroup'),
                        icon: 'Edit2',
                        onPress: () => {
                            editGroupSideDrawer.onOpen();
                        },
                        shouldShow: (selected) => isOrganizationAdmin && selected.length === 1,
                        testID: 'edit-group',
                    },
                    {
                        title: intl('delete'),
                        icon: 'Trash2',
                        onPress: deleteButtonModal.onPress,
                        shouldShow: (selected) => isOrganizationAdmin && selected.length > 0,
                        shouldDisable: () => isDeletingGroup || isDeletingGroups,
                        testID: 'delete-group',
                    },
                ]}
            />
            {addGroupSideDrawer.SideDrawer}
            {editGroupSideDrawer.SideDrawer}
            {groupDetailsSideDrawer.SideDrawer}
            {deleteButtonModal.Prompt}
        </>
    );
};

export default React.memo(GroupsTable);
